import { useState } from 'react';
import { App, Layout, Menu, Flex, Typography, Dropdown, Badge, Button, Drawer } from 'antd';
import {
  AppstoreOutlined,
  CloseOutlined,
  EllipsisOutlined,
  InboxOutlined,
  LogoutOutlined,
  MenuOutlined,
  QuestionCircleOutlined,
  ShopOutlined,
  ShoppingCartOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useStoreContext } from '@/shared/lib/store-context';
import { useGetMeQuery, useLogoutMutation } from '@/entities/user';
import { useOrderRequestCountQuery } from '@/entities/order-request';
import { useJoinRequestCountQuery } from '@/entities/join-request';
import { useGetAiStatusQuery } from '@/entities/ai-assistant';
import { ownerPath, ROUTES } from '@/shared/config/routes';
import { WindowManagerProvider } from '@/app/providers/WindowManagerProvider';
import { MOBILE_BAR_Z_INDEX, MOBILE_TAB_BAR_HEIGHT, MOBILE_TASKBAR_HEIGHT } from '@/app/providers/zLayers';
import { useBreakpoint } from '@/shared/lib/hooks';
import { HotkeyProvider, useActiveBindings, useHotkeyActions, useHotkeyScope, type ActionImplMap, type HotkeyBinding } from '@/shared/lib/hotkeys';
import { useWindowManager } from '@/shared/lib/windows';
import { ComboKbd, HotkeysHelpDialog, HotkeyTip } from '@/shared/ui/hotkey';
import { ALL_NAV, DOCUMENT_NAV, REFERENCE_NAV, REPORT_NAV, REQUEST_NAV, SINGLE_NAV, type NavLeaf } from './ownerNav';

const { Sider, Header, Content } = Layout;

const PARENT_KEYS = ['references', 'documents', 'requests', 'reports'];

/** Телефон: вкладки нижней панели — самые частые разделы (пункты из `ALL_NAV`), «Ещё» открывает всё меню. */
const MOBILE_TABS: { action: NavLeaf['action']; label: string; icon: React.ReactNode }[] = [
  { action: 'nav.sales', label: 'Продажи', icon: <ShoppingCartOutlined /> },
  { action: 'nav.purchases', label: 'Приходы', icon: <InboxOutlined /> },
  { action: 'nav.counterparties', label: 'Контрагенты', icon: <TeamOutlined /> },
  { action: 'nav.categories', label: 'Категории', icon: <AppstoreOutlined /> },
];

/** Телефон: кнопки шапки — цель касания 44×44 (Apple HIG). */
const MOBILE_ICON_BUTTON: React.CSSProperties = { width: 44, height: 44, flex: '0 0 44px', padding: 0 };

/** Телефон: название магазина в шапке и в меню — одна строка с обрезкой. */
const MOBILE_TITLE: React.CSSProperties = {
  flex: '1 1 auto',
  minWidth: 0,
  fontSize: 16,
  fontWeight: 600,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

/** Вкладка нижней панели (как tab bar в iOS): значок над подписью, активная подсвечена. */
function MobileTab({ label, icon, active, onClick }: { label: string; icon: React.ReactNode; active: boolean; onClick: () => void }) {
  const color = active ? '#1677ff' : '#595959';
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      style={{
        flex: '1 1 0',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        padding: 0,
        border: 'none',
        borderTop: `2px solid ${active ? '#1677ff' : 'transparent'}`,
        background: 'none',
        color,
        font: 'inherit',
        cursor: 'pointer',
      }}
    >
      <span style={{ fontSize: 20, lineHeight: 1, display: 'inline-flex' }}>{icon}</span>
      <span style={{ fontSize: 11, lineHeight: '14px', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: active ? 600 : 400 }}>
        {label}
      </span>
    </button>
  );
}

/** §1: владельческий раздел — плотный сайдбар; порядок разделов внутри групп — по ТЗ, группировка — UI-группировка для краткости списка.
 *
 *  Телефон (< 768px): вместо сайдбара — компактная шапка (гамбургер, название магазина, меню
 *  аккаунта), выезжающее слева меню (`Drawer` с теми же пунктами и бейджами, «Мои магазины»,
 *  «Горячие клавиши», «Выйти») и нижняя панель вкладок с частыми разделами и «Ещё». */
export function OwnerLayout() {
  return (
    <HotkeyProvider>
      <WindowManagerProvider>
        <OwnerLayoutContent />
      </WindowManagerProvider>
    </HotkeyProvider>
  );
}

/** Внутри провайдеров — иначе клик по «Отчётам» в сайдбаре (открывает плавающее окно, не
 *  страницу) не достучится до `useWindowManager()`. */
function OwnerLayoutContent() {
  const { modal } = App.useApp();
  const ctx = useStoreContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [logout] = useLogoutMutation();
  const { data: me } = useGetMeQuery();
  const windowManager = useWindowManager();
  const { data: orderRequestCount } = useOrderRequestCountQuery(undefined, { pollingInterval: 60_000 });
  const { data: joinRequestCount } = useJoinRequestCountQuery(undefined, { pollingInterval: 60_000 });
  // ИИ-ассистент: пункт меню и клавиша есть, только если он включён для магазина (иначе окно открывало бы пустую заглушку).
  const { data: aiStatus } = useGetAiStatusQuery();
  const aiEnabled = aiStatus?.enabled === true;
  const isNavVisible = (leaf: NavLeaf) => leaf.window !== 'aiAssistant' || aiEnabled;
  const { isMobile } = useBreakpoint();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  // Справка F1: снимок того, что работало там, где нажали (после открытия диалога фокус уже в нём).
  const activeBindings = useActiveBindings();
  const [help, setHelp] = useState<{ open: boolean; bindings: HotkeyBinding[] }>({ open: false, bindings: [] });

  const keyOf = (leaf: NavLeaf) => ownerPath(ctx.storeId, leaf.path);
  const leafItem = (leaf: NavLeaf, label: React.ReactNode = leaf.label) => ({
    key: keyOf(leaf),
    label,
    // Клавиша перехода справа в пункте; длинную подпись можно прочитать в нативной подсказке.
    title: leaf.label,
    // На телефоне плашек клавиш нет (клавиатуры нет, а место нужно названию).
    extra: isMobile ? undefined : <ComboKbd id={leaf.action} />,
  });
  const badged = (text: string, count: number | undefined) => (
    <Flex align="center" gap={6}>
      {text}
      {count && count > 0 ? <Badge count={count} size="small" /> : null}
    </Flex>
  );

  const requestBadgeTotal = (orderRequestCount?.new ?? 0) + (joinRequestCount?.pending ?? 0);

  const items = [
    { key: 'references', label: 'Справочники', children: REFERENCE_NAV.map((l) => leafItem(l)) },
    { key: 'documents', label: 'Документы', children: DOCUMENT_NAV.map((l) => leafItem(l)) },
    {
      key: 'requests',
      label: badged('Заявки', requestBadgeTotal),
      children: REQUEST_NAV.map((l) => leafItem(l, badged(l.label, l.action === 'nav.orderRequests' ? orderRequestCount?.new : joinRequestCount?.pending))),
    },
    { key: 'reports', label: 'Отчёты', children: REPORT_NAV.map((l) => leafItem(l)) },
    ...SINGLE_NAV.filter(isNavVisible).map((l) => leafItem(l)),
  ];

  const selectedKey = ALL_NAV.map(keyOf).find((key) => location.pathname.startsWith(key));
  const groups: [string, NavLeaf[]][] = [
    ['references', REFERENCE_NAV],
    ['documents', DOCUMENT_NAV],
    ['requests', REQUEST_NAV],
    ['reports', REPORT_NAV],
  ];
  const defaultOpenKeys = groups.filter(([, leaves]) => leaves.some((l) => location.pathname.startsWith(keyOf(l)))).map(([key]) => key);

  function goTo(leaf: NavLeaf) {
    if (leaf.window === 'aiAssistant') {
      windowManager.open({ kind: 'aiAssistant' });
      return;
    }
    if (leaf.report) {
      windowManager.open({ kind: 'report', report: leaf.report });
      return;
    }
    void navigate(keyOf(leaf));
  }

  /** Выход спрашивает подтверждение, только если в открытых окнах есть несохранённое. */
  function handleLogout() {
    if (!windowManager.hasUnsavedChanges) {
      void logout();
      return;
    }
    modal.confirm({
      title: 'Выйти?',
      content: 'В открытых окнах есть несохранённые изменения — они будут потеряны.',
      okText: 'Выйти',
      cancelText: 'Остаться',
      okButtonProps: { danger: true },
      autoFocusButton: 'cancel',
      onOk: () => void logout(),
    });
  }

  // Глобальные клавиши интерфейса владельца: переходы по меню, новая продажа/приход, меню пользователя.
  const navImpl: ActionImplMap = Object.fromEntries(ALL_NAV.filter(isNavVisible).map((leaf) => [leaf.action, { hint: false, run: () => goTo(leaf) }]));
  const globalActions = useHotkeyActions({
    ...navImpl,
    'app.newSale': { hint: false, run: () => windowManager.open({ kind: 'document', type: 'SALE' }) },
    'app.newPurchase': { hint: false, run: () => windowManager.open({ kind: 'document', type: 'PURCHASE' }) },
    'app.userMenu': { hint: false, run: () => setUserMenuOpen(true) },
    'app.logout': { hint: false, run: handleLogout },
    'app.stores': { hint: false, run: () => void navigate(ROUTES.stores) },
    'app.help': { hint: false, run: () => setHelp({ open: true, bindings: activeBindings() }) },
  });
  useHotkeyScope(globalActions.bindings, true, { level: 'global' });

  if (isMobile) {
    const tabLeaves = MOBILE_TABS.map((tab) => ({ tab, leaf: ALL_NAV.find((l) => l.action === tab.action)! }));
    const taskbarShown = windowManager.minimized.length > 0;
    return (
      <Layout style={{ minHeight: '100dvh' }}>
        <header
          style={{
            position: 'sticky',
            // Под плашкой «Нет подключения» (она публикует свою высоту), иначе закрыла бы кнопки шапки.
            top: 'var(--offline-banner-h, 0px)',
            zIndex: MOBILE_BAR_Z_INDEX,
            background: '#fff',
            borderBottom: '1px solid #d9d9d9',
            paddingTop: 'env(safe-area-inset-top, 0px)',
            paddingLeft: 'env(safe-area-inset-left, 0px)',
            paddingRight: 'env(safe-area-inset-right, 0px)',
          }}
        >
          <Flex align="center" gap={4} style={{ height: 48, padding: '0 4px' }}>
            <Button type="text" size="large" icon={<MenuOutlined />} aria-label="Меню" onClick={() => setNavOpen(true)} style={MOBILE_ICON_BUTTON} />
            <div style={MOBILE_TITLE}>{ctx.storeName}</div>
            <Dropdown
              trigger={['click']}
              placement="bottomRight"
              open={userMenuOpen}
              onOpenChange={setUserMenuOpen}
              menu={{
                items: [
                  { key: 'me', label: me?.name ?? 'Аккаунт', disabled: true },
                  { key: 'stores', icon: <ShopOutlined />, label: 'Мои магазины', onClick: () => void navigate(ROUTES.stores) },
                  { key: 'logout', icon: <LogoutOutlined />, label: 'Выйти', danger: true, onClick: handleLogout },
                ],
              }}
            >
              <Button type="text" size="large" icon={<UserOutlined />} aria-label="Меню пользователя" style={MOBILE_ICON_BUTTON} />
            </Dropdown>
          </Flex>
        </header>

        <Content
          style={{
            minWidth: 0,
            padding: 12,
            paddingLeft: 'calc(12px + env(safe-area-inset-left, 0px))',
            paddingRight: 'calc(12px + env(safe-area-inset-right, 0px))',
            // Запас под нижнюю панель вкладок и ленту свёрнутых окон — чтобы последние строки не прятались за ними.
            paddingBottom: `calc(${MOBILE_TAB_BAR_HEIGHT + (taskbarShown ? MOBILE_TASKBAR_HEIGHT : 0) + 12}px + env(safe-area-inset-bottom, 0px))`,
          }}
        >
          <Outlet />
        </Content>

        <nav
          aria-label="Основные разделы"
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: MOBILE_BAR_Z_INDEX,
            display: 'flex',
            height: `calc(${MOBILE_TAB_BAR_HEIGHT}px + env(safe-area-inset-bottom, 0px))`,
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            paddingLeft: 'env(safe-area-inset-left, 0px)',
            paddingRight: 'env(safe-area-inset-right, 0px)',
            background: '#fff',
            borderTop: '1px solid #d9d9d9',
          }}
        >
          {tabLeaves.map(({ tab, leaf }) => (
            <MobileTab key={tab.action} label={tab.label} icon={tab.icon} active={selectedKey === keyOf(leaf)} onClick={() => goTo(leaf)} />
          ))}
          <MobileTab
            label="Ещё"
            icon={
              <Badge count={requestBadgeTotal} size="small" offset={[4, -2]}>
                <EllipsisOutlined />
              </Badge>
            }
            // Открыт раздел, которого нет среди вкладок, — он «под» «Ещё».
            active={selectedKey !== undefined && !tabLeaves.some(({ leaf }) => keyOf(leaf) === selectedKey)}
            onClick={() => setNavOpen(true)}
          />
        </nav>

        <Drawer
          open={navOpen}
          onClose={() => setNavOpen(false)}
          placement="left"
          closable={false}
          width="min(320px, 86vw)"
          destroyOnHidden
          styles={{ header: { display: 'none' }, body: { padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' } }}
        >
          <Flex
            align="center"
            gap={4}
            style={{
              flex: '0 0 auto',
              minHeight: 48,
              padding: 'env(safe-area-inset-top, 0px) 4px 0 calc(16px + env(safe-area-inset-left, 0px))',
              borderBottom: '1px solid #d9d9d9',
            }}
          >
            <div style={MOBILE_TITLE}>{ctx.storeName}</div>
            <Button type="text" size="large" icon={<CloseOutlined />} aria-label="Закрыть меню" onClick={() => setNavOpen(false)} style={MOBILE_ICON_BUTTON} />
          </Flex>
          <div
            style={{
              flex: '1 1 auto',
              minHeight: 0,
              overflowY: 'auto',
              overscrollBehavior: 'contain',
              paddingBottom: 'env(safe-area-inset-bottom, 0px)',
              paddingLeft: 'env(safe-area-inset-left, 0px)',
            }}
          >
            <Menu
              mode="inline"
              selectedKeys={selectedKey ? [selectedKey] : []}
              defaultOpenKeys={defaultOpenKeys}
              items={items}
              onClick={({ key }) => {
                if (PARENT_KEYS.includes(key)) return;
                const leaf = ALL_NAV.find((l) => keyOf(l) === key);
                if (!leaf) return;
                setNavOpen(false);
                goTo(leaf);
              }}
              style={{ borderInlineEnd: 'none' }}
            />
            <Menu
              mode="inline"
              selectable={false}
              items={[
                { key: 'stores', icon: <ShopOutlined />, label: 'Мои магазины' },
                { key: 'hotkeys', icon: <QuestionCircleOutlined />, label: 'Горячие клавиши' },
                { key: 'logout', icon: <LogoutOutlined />, label: 'Выйти', danger: true },
              ]}
              onClick={({ key }) => {
                if (key === 'hotkeys') {
                  // Снимок привязок — пока меню ещё открыто, как у F1: «что работало там, где нажали».
                  const bindings = activeBindings();
                  setNavOpen(false);
                  setHelp({ open: true, bindings });
                  return;
                }
                setNavOpen(false);
                if (key === 'stores') void navigate(ROUTES.stores);
                if (key === 'logout') handleLogout();
              }}
              style={{ borderInlineEnd: 'none', borderTop: '1px solid #d9d9d9' }}
            />
          </div>
        </Drawer>
        <HotkeysHelpDialog open={help.open} bindings={help.bindings} onClose={() => setHelp((h) => ({ ...h, open: false }))} />
      </Layout>
    );
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider width={232} theme="light" style={{ borderRight: '1px solid #d9d9d9' }}>
        <Menu
          mode="inline"
          selectedKeys={selectedKey ? [selectedKey] : []}
          defaultOpenKeys={defaultOpenKeys}
          items={items}
          onClick={({ key }) => {
            if (PARENT_KEYS.includes(key)) return;
            const leaf = ALL_NAV.find((l) => keyOf(l) === key);
            if (leaf) goTo(leaf);
          }}
          style={{ borderInlineEnd: 'none' }}
        />
      </Sider>
      <Layout>
        <Header style={{ background: '#fff', borderBottom: '1px solid #d9d9d9', padding: '0 16px', height: 40, lineHeight: '40px' }}>
          <Flex justify="space-between" align="center">
            <Typography.Text strong>{ctx.storeName}</Typography.Text>
            <Flex gap={8} align="center">
              <HotkeyTip action={globalActions.get('app.stores')} placement="bottom" display="inline-flex">
                <Button type="link" size="small" onClick={() => void navigate(ROUTES.stores)}>
                  Мои магазины
                </Button>
              </HotkeyTip>
              <Dropdown
                trigger={['click']}
                open={userMenuOpen}
                onOpenChange={setUserMenuOpen}
                menu={{
                  items: [{ key: 'logout', label: 'Выйти', extra: <ComboKbd id="app.logout" />, onClick: handleLogout }],
                }}
              >
                <span style={{ display: 'inline-flex' }}>
                  <HotkeyTip action={globalActions.get('app.userMenu')} placement="bottom" display="inline-flex">
                    <Button type="text" size="small" icon={<UserOutlined />} aria-label="Меню пользователя">
                      {me?.name ?? 'Аккаунт'}
                    </Button>
                  </HotkeyTip>
                </span>
              </Dropdown>
            </Flex>
          </Flex>
        </Header>
        <Content style={{ padding: 12 }}>
          <Outlet />
        </Content>
      </Layout>
      <HotkeysHelpDialog open={help.open} bindings={help.bindings} onClose={() => setHelp((h) => ({ ...h, open: false }))} />
    </Layout>
  );
}
